import { createApiClient } from '../apis/infrastructure/createApiClient';
import { API_URLS } from '../apis/apiUrls';
import { RESPONSE_HEADER_KEYS } from '../apis/constants/apiHeaders';

/**
 * Maps raw user profile data and impersonation secret into a normalized object.
 *
 * @param {Object} userProfile - User profile returned by the API.
 * @param {string|undefined} userSecret - Optional impersonation secret from response headers.
 * @returns {Object} Normalized user profile data.
 */
const mapUserProfile = (userProfile, userSecret) => ({
	firstName: userProfile.firstName,
	lastName: userProfile.lastName,
	email: userProfile.email,
	company: userProfile.company,
	canImpersonate: userProfile.canImpersonate,
	canOrder: userProfile.canOrder,
	companyNumber: userProfile.companyNumber,
	userSecret,
});

/**
 * Builds authorization headers for API requests.
 *
 * @param {string} accessToken - Bearer token used to authorize the request.
 * @returns {{ headers: { Authorization: string } }} Request header object.
 */
const getAuthHeaders = (accessToken) => ({
	headers: {
		Authorization: `Bearer ${accessToken}`,
	},
});

/**
 * Requests impersonation for a user by email.
 *
 * @param {string} email - Email address of the user to impersonate.
 * @param {string} accessToken - Bearer token used to authorize the request.
 * @returns {Promise<Object|null>} Impersonated user profile, or null when required inputs are missing.
 * @throws {Error} Throws when the impersonation request fails.
 */
export const impersonateUser = async (email, accessToken) => {
	if (!email || !accessToken) {
		return null;
	}

	try {
		const apiClient = createApiClient();
		const impersonateResponse = await apiClient.post(
			API_URLS.user.impersonate(email),
			null,
			getAuthHeaders(accessToken)
		);

		return mapUserProfile(
			impersonateResponse.data,
			impersonateResponse.headers?.[RESPONSE_HEADER_KEYS.xImpersonated]
		);
	} catch (error) {
		const status = error?.response?.status;
		const statusText = status ? ` (status ${status})` : '';
		const wrappedError = new Error(
			`Impersonate user request failed${statusText}.`
		);
		wrappedError.cause = error;
		throw wrappedError;
	}
};

/**
 * Retrieves the authenticated user profile by email.
 *
 * @param {string} email - Email address of the authenticated user.
 * @param {string} accessToken - Bearer token used to authorize the request.
 * @returns {Promise<Object|null>} Authenticated user profile, or null when required inputs are missing.
 * @throws {Error} Throws when the profile request fails.
 */
export const getAuthenticatedUser = async (email, accessToken) => {
	if (!email || !accessToken) {
		return null;
	}

	try {
		const apiClient = createApiClient();
		const profileResponse = await apiClient.get(
			API_URLS.user.getProfile(email),
			getAuthHeaders(accessToken)
		);

		return mapUserProfile(
			profileResponse.data,
			profileResponse.headers?.[RESPONSE_HEADER_KEYS.xImpersonated]
		);
	} catch (error) {
		const status = error?.response?.status;
		const statusText = status ? ` (status ${status})` : '';
		const wrappedError = new Error(
			`Authenticated user profile request failed${statusText}.`
		);
		wrappedError.cause = error;
		throw wrappedError;
	}
};
